/* Inputs marked as:
 *
 *   <input ... data-auto-submit>
 *
 *  Automatically submit its containing form on "change" event.
 *
 * To specify other events (like "input"), set the "data-auto-submit" attribute to a
 * space-separated list.  "true" is interpreted as "change".
 *
 * Use `data-auto-submit-debounce="N"` (milliseconds) to debounce rapid events
 *
 * The DOM is watched for mutations, so HTML coming from the server side
 * will be processed automatically.
 */

import Rails from "@rails/ujs";
import { debounce } from "lodash-es";

import { onceEach } from "./once.js";
import { onDateChange } from "./on_date_change.js";
import { trackEvent } from "./analytics.js";

const scan = (document) => {
  const selectors = ["input", "textarea", "select"]
    .map((tag) => `${tag}[data-auto-submit]`)
    .join(", ");

  onceEach(document.querySelectorAll(selectors), "auto-submit", (input) => {
    if (input.dataset.autoSubmit == "false") {
      return;
    }

    const { analyticsEvent } = input.dataset;

    let eventNames = (input.dataset.autoSubmit || "").split(/\s+/).filter((k) => k && k !== "true");
    if (eventNames.length === 0) {
      eventNames = ["change"];
    }

    const debounceMs = parseInt(input.dataset.autoSubmitDebounce, 10);
    const hasDebounce = Number.isFinite(debounceMs) && debounceMs > 0;

    const submit = () => {
      // a debounced submit can fire after partial:replace has detached this input
      if (!input.isConnected) return;

      safeSubmitForm(input.form);

      if (analyticsEvent) {
        trackEvent(analyticsEvent);
      }
    };

    const fire = hasDebounce ? debounce(submit, debounceMs) : submit;

    eventNames.forEach((eventName) => {
      if (input.type === "date" && eventName === "change") {
        onDateChange(input, fire);
      } else {
        input.addEventListener(eventName, fire);
      }
    });

    // For number inputs, holding arrow keys or scrolling rapidly fires change events
    // flush any pending submit on blur so tab/click-away doesn't wait for debounce
    if (hasDebounce && input.type === "number" && !eventNames.includes("blur")) {
      input.addEventListener("blur", () => fire.flush());
    }
  });
};

export function safeSubmitForm(form) {
  if (!form.reportValidity()) {
    return false;
  }

  if (form.dataset.remote && form.dataset.remote !== "false") {
    Rails.fire(form, "submit");
  } else if (form.requestSubmit) {
    form.requestSubmit();
  } else {
    form.submit();
  }

  return true;
}

document.addEventListener("DOMContentLoaded", () => scan(document));
document.addEventListener("partial:replace", (ev) => scan(ev.container));
